import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';

import { isNotFound } from '../../../../../api/call.js';
import {
  useCustomPage,
  useDeleteCustomPage,
  useUpdateCustomPage,
} from '../../../../../api/custom-pages.js';
import { Button } from '../../../../../components/Button.js';
import { DangerAlert } from '../../../../../components/DangerAlert.js';
import { Dialog } from '../../../../../components/Dialog.js';
import { PageHeading } from '../../../../../components/PageHeading.js';
import { LoadingArea, TextPage } from '../../../../../components/PlaceholderPage.js';
import styles from '../../../../../custom-data/custom-data.module.css';
import { CustomPageForm } from '../../../../../custom-data/CustomPageForm.js';

/** `/websites/:websiteId/custom-data/:customPageId`: edit or delete one custom page. */
export const Route = createFileRoute('/_app/websites/$websiteId/custom-data/$customPageId')({
  // `saved` is set once, by the New custom page form, so "Saved" shows after the URL changes.
  validateSearch: (search: Record<string, unknown>): { saved?: boolean | undefined } => ({
    saved: z.boolean().optional().catch(undefined).parse(search['saved']),
  }),
  component: EditCustomPage,
});

function EditCustomPage() {
  const { websiteId, customPageId } = Route.useParams();
  const { saved } = Route.useSearch();
  const navigate = useNavigate();
  const page = useCustomPage(websiteId, customPageId);
  const update = useUpdateCustomPage(websiteId, customPageId);
  const remove = useDeleteCustomPage(websiteId, customPageId);
  const [confirming, setConfirming] = useState(false);

  const back = (
    <Link className={styles.back} to="/websites/$websiteId/custom-data" params={{ websiteId }}>
      <ArrowLeft aria-hidden="true" width={14} height={14} strokeWidth={1.75} />
      Back to custom data
    </Link>
  );

  if (page.isError && isNotFound(page.error)) {
    return (
      <>
        {back}
        <TextPage title="Edit custom page">
          This custom page doesn&apos;t exist or was removed.
        </TextPage>
      </>
    );
  }
  if (page.isPending) return <LoadingArea />;
  if (page.isError) {
    return (
      <DangerAlert
        title="Couldn't load this custom page"
        action={<Button onClick={() => void page.refetch()}>Try again</Button>}
      >
        Check your connection and try again.
      </DangerAlert>
    );
  }

  return (
    <>
      {back}
      <div className={styles.header}>
        <PageHeading documentTitle={page.data.title}>Edit custom page</PageHeading>
      </div>
      {saved ? (
        <p className={styles.saved} role="status" style={{ marginBottom: 'var(--s-3)' }}>
          Saved. The assistant uses it from the next answer.
        </p>
      ) : null}
      <CustomPageForm
        key={page.data.id}
        page={page.data}
        isSaving={update.isPending}
        onSave={(body) => update.mutateAsync(body)}
      />

      <section className={styles.danger} aria-labelledby="delete-heading">
        <h2 id="delete-heading">Delete custom page</h2>
        <p>The assistant stops using it at once. This can&apos;t be undone.</p>
        <Button variant="danger" onClick={() => setConfirming(true)}>
          Delete custom page
        </Button>
      </section>

      <Dialog
        title={`Delete custom page '${page.data.title}'?`}
        isOpen={confirming}
        onClose={() => setConfirming(false)}
        showCloseButton={false}
        footer={
          <>
            <Button variant="ghost" autoFocus onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              aria-disabled={remove.isPending ? true : undefined}
              onClick={() => {
                if (remove.isPending) return;
                remove.mutate(undefined, {
                  onSuccess: () =>
                    void navigate({
                      to: '/websites/$websiteId/custom-data',
                      params: { websiteId },
                    }),
                });
              }}
            >
              {remove.isPending ? 'Deleting' : 'Delete custom page'}
            </Button>
          </>
        }
      >
        {remove.isError ? (
          <DangerAlert title="Custom page not deleted">
            Something went wrong. Try again.
          </DangerAlert>
        ) : null}
        <p>The assistant stops using it at once. This can&apos;t be undone.</p>
      </Dialog>
    </>
  );
}
